import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faSync, 
  faTimesCircle, 
  faCalendarAlt, 
  faCreditCard, 
  faBolt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch (_err) {
    return value;
  }
};

const SubscriptionCard = ({ 
  planInfo, 
  loading, 
  syncLoading, 
  onCancel, 
  onSync, 
  onSubscribe,
  onReactivate 
}) => {
  const isActive = planInfo.isActive && planInfo.type === 'pro';
  const isCanceledButActive = isActive && planInfo.cancelAtPeriodEnd;

  // Status configuration
  const statusConfig = isCanceledButActive
    ? {
        label: 'Résiliation programmée',
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/20',
        icon: faExclamationTriangle,
        description: "Votre abonnement s'arrêtera à la fin de la période. Vous conservez vos avantages jusqu'à cette date."
      }
    : isActive
    ? {
        label: 'Abonnement actif',
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20',
        icon: faCheckCircle,
        description: '200 crédits mensuels + no-watermark illimité'
      }
    : {
        label: 'Aucun abonnement actif',
        color: 'text-slate-500',
        bgColor: 'bg-slate-100 dark:bg-slate-800',
        borderColor: 'border-slate-200 dark:border-slate-700',
        icon: faTimesCircle,
        description: 'Passez sur Muzo+ pour profiter des crédits mensuels et du retrait watermark illimité.'
      };

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/60 p-6 shadow-sm transition-all duration-200 hover:shadow-md">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">
              Muzo+
            </span>
            <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
              <FontAwesomeIcon icon={statusConfig.icon} className="text-[10px]" />
              {statusConfig.label}
            </div>
          </div>
          
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {isActive ? 'Muzo+' : 'Plan Gratuit'}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg leading-relaxed">
            {statusConfig.description}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {isActive ? (
            isCanceledButActive ? (
              <button
                type="button"
                onClick={onReactivate}
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white transition hover:bg-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20"
              >
                <FontAwesomeIcon icon={faBolt} />
                {loading ? 'Traitement...' : 'Se réabonner !'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-3 font-semibold text-slate-700 dark:text-gray-200 transition hover:bg-slate-50 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-70"
              >
                {loading ? 'Traitement...' : 'Annuler mon abonnement'}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={onSubscribe}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white transition hover:bg-indigo-500 disabled:opacity-70 shadow-lg shadow-indigo-500/20"
            >
              <FontAwesomeIcon icon={faBolt} />
              {loading ? 'Traitement...' : 'Activer Muzo+'}
            </button>
          )}
          
          <button
            type="button"
            onClick={onSync}
            disabled={syncLoading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-gray-700 px-4 py-3 font-semibold text-slate-600 dark:text-gray-400 transition hover:bg-slate-50 dark:hover:bg-gray-800 hover:text-indigo-500 dark:hover:text-indigo-400 disabled:opacity-70"
            title="Forcer la synchronisation avec Stripe"
          >
            <FontAwesomeIcon icon={faSync} className={syncLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Sync</span>
          </button>
        </div>
      </div>

      {/* Details Grid */}
      {isActive && (
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className={`rounded-xl border ${statusConfig.borderColor} ${statusConfig.bgColor} p-4 transition-colors`}>
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-wide opacity-70">
              <FontAwesomeIcon icon={faCalendarAlt} />
              {isCanceledButActive ? 'Actif jusqu\'au' : 'Prochain renouvellement'}
            </div>
            <p className="text-lg font-bold text-slate-900 dark:text-white">
              {planInfo.nextBilling 
                ? formatDate(planInfo.nextBilling) 
                : (syncLoading ? 'Synchronisation...' : '—')}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/50 p-4">
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
              <FontAwesomeIcon icon={faBolt} />
              Crédits mensuels
            </div>
            <p className="text-lg font-bold text-slate-900 dark:text-white">
              {planInfo.monthlyCredits || 200} crédits
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/50 p-4">
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
              <FontAwesomeIcon icon={faCreditCard} />
              ID Abonnement
            </div>
            <p className="text-xs font-mono text-slate-400 truncate pt-1.5" title={planInfo.stripeSubscriptionId}>
              {planInfo.stripeSubscriptionId || 'Non configuré'}
            </p>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="mt-6 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 p-4 text-sm text-indigo-900 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-500/20">
        <p className="font-semibold mb-2 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} className="text-indigo-500" />
          Comment c'est géré ?
        </p>
        <ul className="grid gap-2 sm:grid-cols-2 text-xs opacity-90">
          <li className="flex items-start gap-2">
            <span className="block w-1 h-1 mt-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
            Stripe envoie un webhook invoice.paid chaque mois.
          </li>
          <li className="flex items-start gap-2">
            <span className="block w-1 h-1 mt-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
            +200 crédits ajoutés automatiquement à chaque paiement.
          </li>
          <li className="flex items-start gap-2">
            <span className="block w-1 h-1 mt-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
            Retrait de watermark illimité inclus pour les abonnés.
          </li>
          <li className="flex items-start gap-2">
            <span className="block w-1 h-1 mt-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
            En cas d'annulation, l'accès reste actif jusqu'à la fin.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SubscriptionCard;
